import { useQuery } from '@tanstack/react-query';
import { memo, useEffect, useState, useCallback } from 'react';

import { Box, Grid, Pagination } from '@core/component';
import { ARTICLES_TO_SHOW } from '@core/constant';
import {
  useContextAlternate,
  useContextBreadcrumb,
  useContextMediaQuery,
  useContextRouting,
  useContextMeta,
} from '@core/context';
import { useLanguage } from '@core/hook';
import { getSanityQuery, formatAlternates } from '@core/sanity';
import { ViewCMSProps, ERoutingDynamic } from '@core/type';
import { SanityBlogCategory, SanityBlogCategoryArticles } from '@core/type/sanity';
import { SpotlightBanner } from '@core/widget';

import { BlogArticle } from './BlogArticle';

export const ViewBlogCategory = memo(({ cms }: ViewCMSProps<SanityBlogCategory>) => {
  const [isSubscriptionSuccessful, setIsSubscriptionSuccessful] = useState(false);

  const { query, setQuery, keyRoute } = useContextRouting();
  const { isPhone } = useContextMediaQuery();
  const { language } = useLanguage();
  const { setAlternates } = useContextAlternate();
  const { setBreadcrumbs } = useContextBreadcrumb();
  const { setMeta } = useContextMeta();

  const perPage = ARTICLES_TO_SHOW;
  const { data: blogArticles } = useQuery({
    ...getSanityQuery<SanityBlogCategoryArticles>('blogCategoryArticlesQuery', {
      page: query.page as string,
      perPage,
      categoryId: cms._id,
    }),
    keepPreviousData: true,
  });

  const changePage = useCallback(
    (page: number) => {
      setQuery({ slug: query?.slug, ...(page === 1 ? {} : { page: page.toString() }) }, null, true);
    },
    [language, query],
  );

  useEffect(() => {
    if (query.page && blogArticles?.length <= 0) {
      changePage(1);
    }
    setAlternates(formatAlternates(cms.alternates?.alt, keyRoute));
    setBreadcrumbs([
      {
        breadcrumbType: ERoutingDynamic.BLOG_CATEGORY,
        index: 1,
        slug: cms.pageSlug.slug.current,
        title: cms.title,
      },
    ]);
    setMeta();
  }, [blogArticles, cms]);

  return (
    <Box>
      {blogArticles?.length > 0 && (
        <Box paddingBottom={isPhone ? '104px' : isSubscriptionSuccessful ? '224px' : '144px'}>
          <Grid isContainer rowSpacing={64} columnSpacing={30}>
            {blogArticles.map((article) => (
              <Grid xs={12} md={6} lg={4} key={article.pageSlug?.slug.current}>
                <BlogArticle article={article} />
              </Grid>
            ))}
          </Grid>

          <Box marginTop={isPhone ? '56px' : '80px'}>
            <Pagination
              page={Number(query?.page) || 1}
              perPage={perPage}
              itemsCount={cms.articlesCount}
              onPageChange={changePage}
            />
          </Box>
        </Box>
      )}

      {!isSubscriptionSuccessful && <SpotlightBanner fn={setIsSubscriptionSuccessful} />}
    </Box>
  );
});

ViewBlogCategory.displayName = 'ViewBlogCategory';
